import { MathUtil } from "@base/math/MathUtil";
import { Debug } from "@biz/Debug";
import { IMinMax } from "@biz/definitions/MinMax";
import { IAxlePos } from "@biz/definitions/MountDefinitons";
import { SST } from "@biz/definitions/SST";
import { IDistInOut, LengthBType, MaterialGroup, materialInfos, MaterialType, MAX_POLE_LENGTH, PoleId, PoleIndex, PoleMountType, PoleType, SunSailType } from "@biz/definitions/SunSailDefinitions";
import { Poles } from "@biz/items/sunSail/logic/Poles";
import { Measure } from "@biz/logic/Measure";

export class PoleLogic {

  static isSteel(material: MaterialType | undefined): boolean {
    return PoleLogic.isMaterial(material, "steel");
  }

  static isMaterial(material: MaterialType | undefined, materialGroup: MaterialGroup): boolean {
    if (!material)
      return false;
    return materialInfos[material]?.group === materialGroup;
  }


  static isAlu(material: MaterialType | undefined): boolean {
    return PoleLogic.isMaterial(material, "alu");
  }

  static isAluOrSteel(material: MaterialType | undefined): boolean {
    return PoleLogic.isSteel(material) || PoleLogic.isAlu(material);
  }

  static isDroppole(material: MaterialType | undefined): boolean {
    return PoleLogic.isMaterial(material, "droppole");
  }

  static isIron(material: MaterialType | undefined): boolean {
    return PoleLogic.isMaterial(material, "iron48") || PoleLogic.isMaterial(material, "iron89") || PoleLogic.isMaterial(material, "iron114");
  }

  static isIron89(material: MaterialType | undefined): boolean {
    return PoleLogic.isMaterial(material, "iron89")
  }

  static isTelescope(material: MaterialType | undefined): boolean {
    return PoleLogic.isMaterial(material, "telescope");
  }

  static isProfil(material: MaterialType | undefined): boolean {
    return PoleLogic.isMaterial(material, "profile");
  }

  static isTeleProfil(material: MaterialType | undefined): boolean {
    return PoleLogic.isTelescope(material) || PoleLogic.isProfil(material);
  }

  static getPoleDiameterMili(material: MaterialType | undefined): number {
    if (PoleLogic.isSteel(material)) return 70;
    else if (PoleLogic.isAlu(material)) return 78;
    else if (PoleLogic.isDroppole(material)) return 120;
    else if (PoleLogic.isMaterial(material, "iron114")) return 114;
    else if (PoleLogic.isMaterial(material, "iron89")) return 89;
    else if (PoleLogic.isMaterial(material, "iron48")) return 48;
    else if (PoleLogic.isTeleProfil(material)) return 78;
    return 0;
  }

  static getPoleStrengthName(material: MaterialType): string {
    const poleStrength = PoleLogic.getPoleDiameterMeter(material);
    if (poleStrength === 0) return "";
    return `Ø ${poleStrength}${Measure.mm}`;
  }

  static getPoleDiameterMeter(material: MaterialType, poleType?: PoleType): number {
    if (poleType !== undefined && poleType !== PoleType.Pole)
      return 0;
    return PoleLogic.getPoleDiameterMili(material) / 1000;
  }

  static getMaxPoleLength(material: MaterialType): number {
    if (PoleLogic.isTelescope(material))
      return 3.50;
    if (PoleLogic.isProfil(material))
      return 3.70;
    if (PoleLogic.isDroppole(material))
      return 5;
    return MAX_POLE_LENGTH;
  }

  /** @deprecated */
  static isBent(angle: number, lengthB: LengthBType | undefined): boolean {
    return angle > 0.001 && PoleLogic.calcLengthB(lengthB) > 0;
  }

  /** @deprecated */
  static canBeBent(poleType: PoleType, material: MaterialType, mount: PoleMountType): boolean {
    return poleType === PoleType.Pole && PoleLogic.getMinBendHeightOverGround(material, mount) !== undefined;
  }

  /** @deprecated */
  static calcLengthB(lengthB: LengthBType | undefined): number {
    if (!lengthB)
      return 0;
    if (typeof lengthB === "number")
      return lengthB;
    return lengthB.enabled && lengthB.val >= 0.01 ? lengthB.val : 0;
  }

  static isHeightChangeValidOld(sunSailType: SunSailType, poleIndex: number, poleType: PoleType, material: MaterialType | undefined, isPylon: boolean | undefined): boolean {
    if (poleType !== PoleType.Pole && poleType !== PoleType.Wall) // SpecialConsole?
      return false;

    switch (material) {
      case "Steel":
      case "Alu":
      case "Alu_Black":
      case "AluWood":
      case "Droppole":
      case "Droppole_Black":
      case "Steel_V4A":
        break;
      default:
        return false;
    }

    if (SST.is(sunSailType, SST.CoSF))
      return true;
    else if (SST.is(sunSailType, SST.C, SST.CsTwin))
      return !!isPylon && !PoleLogic.isAxle(sunSailType, poleIndex);
    else if (SST.is(sunSailType, SST.One)) {
      return !PoleLogic.isAxle(sunSailType, poleIndex)/* && SunSailLogic.getOperatingDeflectionPosition(ssd) !== poleIndex*/;
    } else if (SST.is(sunSailType, SST.AM, SST.XXs))
      return !PoleLogic.isAxle(sunSailType, poleIndex);
    return false;
  }

  private static axleIndicesAC: IAxlePos<PoleIndex> = { first: PoleIndex.A, second: PoleIndex.C };
  private static axleIndicesAB: IAxlePos<PoleIndex> = { first: PoleIndex.A, second: PoleIndex.B };
  static getAxleIndices(sunSailType: SunSailType): IAxlePos<PoleIndex> | undefined {
    switch (sunSailType) {
      case SunSailType.SolidayA:
      case SunSailType.SolidayM:
      case SunSailType.SolidayC:
      case SunSailType.SolidayX:
      case SunSailType.SolidayOne:
      case SunSailType.SolidayFlex:
        return PoleLogic.axleIndicesAC; // same ref everytime
      case SunSailType.SolidayCS:
      case SunSailType.SolidayTwin:
      case SunSailType.SolidayXS:
        return PoleLogic.axleIndicesAB;  // same ref everytime
    }
    return undefined;
  }

  static isAxle(sunSailType: SunSailType, poleId: PoleId): boolean {
    if (!Poles.isCornerPole(poleId))
      return false;

    const ret = PoleLogic.getAxleIndices(sunSailType);
    if (!ret) return false;
    return ret.first === poleId || ret.second === poleId;
  }


  static isPylonRequired(sunSailType: SunSailType, poleId: PoleId, poleType: PoleType): boolean {
    if (!Poles.isCornerPole(poleId))
      return false;
    if (poleType !== PoleType.Pole)
      return false;

    switch (sunSailType) {
      case SunSailType.SolidayCS:
      case SunSailType.SolidayTwin:
        return !PoleLogic.isAxle(sunSailType, poleId)
    }
    return false;
  }

  static isPylonValid(sunSailType: SunSailType, poleId: PoleId, poleType: PoleType): boolean {
    if (!Poles.isCornerPole(poleId))
      return false;
    if (poleType !== PoleType.Pole)
      return false;
    if (this.isPylonRequired(sunSailType, poleId, poleType))
      return true;
    switch (sunSailType) {
      case SunSailType.SolidayC:
        return true;
    }
    return false;
  }

  static isWallMountPossible(sunSailType: SunSailType, poleId: PoleId): boolean {
    if (!Poles.isCornerPole(poleId))
      return false;

    return !PoleLogic.isPylonRequired(sunSailType, poleId, PoleType.Pole)
  }

  static isPoleMountPossible(material: MaterialType, mount: PoleMountType): boolean {
    if (PoleLogic.isDroppole(material) && mount === PoleMountType.GroundSleeve)
      return false;
    return true;
  }

  static getMinMaxOrArray(v: number | number[] | IMinMax | undefined): number[] | IMinMax {
    if (v === undefined)
      return { min: Number.MIN_VALUE, max: Number.MAX_VALUE }
    if (Array.isArray(v)) {
      if (v.length >= 2)
        return v;
      return { min: v[0], max: v[0] };
    } else if (typeof v === "number") {
      return { min: v, max: v }
    } else {
      return v;
    }
  }

  static getMinMax(v: number | number[] | IMinMax | undefined): IMinMax & { fixed?: boolean } {
    if (v === undefined)
      return { min: Number.MIN_VALUE, max: Number.MAX_VALUE }
    if (Array.isArray(v)) {
      if (v.length >= 2)
        return { min: v[0], max: v[v.length - 1], fixed: true };
      return { min: v[0], max: v[0] };
    } else if (typeof v === "number") {
      return { min: v, max: v }
    } else {
      return { min: v.min, max: v.max };
    }
  }

  static hasDistanceOut(sunSailType: SunSailType, poleIndex: number): boolean {
    switch (sunSailType) {
      case SunSailType.SolidayA:
      case SunSailType.SolidayX:
      case SunSailType.SolidayC:
      case SunSailType.SolidayM:
      case SunSailType.SolidayOne:
      case SunSailType.SolidayFlex:
        return !PoleLogic.isAxle(sunSailType, poleIndex);
      case SunSailType.SolidayCS:
      case SunSailType.SolidayXS:
      case SunSailType.SolidayTwin:
        return !PoleLogic.isAxle(sunSailType, poleIndex);
      case SunSailType.Standard:
        return false;
      case SunSailType.Fixed:
        return false;
      case SunSailType.Concept:
        return false;
      case SunSailType.RaffC:
      case SunSailType.RaffM:
        return poleIndex === PoleIndex.A || poleIndex === PoleIndex.D;
      default:
        Debug.notSupported();
    }
    return false;
  }

  static hasDistanceIn(sunSailType: SunSailType, _poleIndex: number): boolean {
    switch (sunSailType) {
      case SunSailType.Standard:
        return false;
    }
    return true;
  }

  static calcMinBendLength(material: MaterialType, angle: number): number {
    if (PoleLogic.isAlu(material)) {
      const deg = MathUtil.rad2deg(angle);
      if (deg <= 5.1)
        return 0.10;
      else if (deg <= 10.1)
        return 0.16;
      else if (deg <= 15.1)
        return 0.20;
      else
        return 0.22;
    } else if (PoleLogic.isSteel(material)) {
      return 0.0; // https://plaspack.atlassian.net/browse/APP22-322
    }
    return 0;
  }

  static getMinBendHeightOverGround(material: MaterialType, mount: PoleMountType): number | undefined {
    if (PoleLogic.isSteel(material)) {
      switch (mount) {
        case "None": return 0.3;
        case "Clamp": return 0.3;
        case "BaseCube": return 0.56;
      }
    } else if (PoleLogic.isAlu(material)) {
      if (material === "Alu_Black") // Alu anthrazit can't be bent... https://plaspack.atlassian.net/browse/APP22-221
        return undefined;
      switch (mount) {
        case "None": return 0.3;
        case "Clamp": return 0.3;
        case "BaseCube": return 0.56;
      }
    } else if (PoleLogic.isIron(material)) {
      // none
    }

    return undefined;
  }

  static getAllowedLengthB(sunSailType: SunSailType, poleType: PoleType, material: MaterialType, mount: PoleMountType, hasLengthB: boolean, angle: number): IMinMax | number[] | number {
    if (SST.isRaff(sunSailType)) {
      return 0;
    }

    if (poleType !== PoleType.Pole) {
      return 0;
    }

    if (PoleLogic.isDroppole(material))
      return 0;
    else if (PoleLogic.isSteel(material)) {
      switch (mount) {
        case "None": return hasLengthB ? { min: 0.3, max: MAX_POLE_LENGTH } : { min: 0.0, max: MAX_POLE_LENGTH };
        case "Clamp": return hasLengthB ? { min: 0.3, max: MAX_POLE_LENGTH } : { min: 0.0, max: MAX_POLE_LENGTH };
        case "ConcreteCube": return { min: 0.0, max: MAX_POLE_LENGTH };
        case "BaseCube": return 0.56;
      }
    }
    else if (PoleLogic.isAlu(material)) {
      switch (mount) {
        case "None": return { min: 0.5, max: MAX_POLE_LENGTH };
        case "Clamp": return hasLengthB ? { min: 0.5, max: MAX_POLE_LENGTH } : { min: 0.0, max: MAX_POLE_LENGTH };
        case "BaseCube": return 0.56 + (hasLengthB ? PoleLogic.calcMinBendLength(material, angle) : 0)
      }
    }
    return 0;
  }

  static hadDistIn2(dist: IDistInOut): boolean {
    return (dist.type === "in" && Array.isArray(dist.val) && dist.val[0] !== dist.val[1]);
  }
}